import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

interface User {
  name: string;
  lastLogin: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('travel-portal-auth');
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);
      setIsAuthenticated(true);
      setUser(authData.user);
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    // Updated credentials to match Ervin user from image
    if (username === 'briii123' && password === 'password123') {
      const userData: User = {
        name: 'Briii',
        lastLogin: 'Today, 9:30 AM'
      };

      setIsAuthenticated(true);
      setUser(userData);

      // Save to localStorage for persistence
      localStorage.setItem('travel-portal-auth', JSON.stringify({
        isAuthenticated: true,
        user: userData,
        timestamp: new Date().toISOString()
      }));

      return true;
    }
    return false;
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    // In a real application, this would make an API call to verify current password
    // and update to the new password. For now, we'll simulate this behavior.
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // For demo purposes, we'll check if current password matches the hardcoded one
    if (currentPassword === 'password123') {
      // In a real app, the new password would be sent to the server
      // For now, we'll just return success (newPassword would be used in real implementation)
      console.log('Password would be changed to:', newPassword);
      return true;
    }
    
    return false; // Current password is incorrect
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('travel-portal-auth');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};